<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { Icon } from '@iconify/vue';
import axios from 'axios';

const props = defineProps({
    bankAccounts: Array,
    anioEscolar: Number,
    permissions: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['accounts-updated']);

// Cuentas bancarias reactivas locales
const accounts = ref([...(props.bankAccounts || [])]);

watch(() => props.bankAccounts, (newVal) => {
    accounts.value = [...(newVal || [])];
});

const fetchBankAccounts = async () => {
    try {
        const res = await axios.get(route('financiera-bank-accounts.list'));
        accounts.value = res.data;
        emit('accounts-updated', res.data);
    } catch (err) {
        console.error('Error al actualizar cuentas bancarias:', err);
    }
};

// Estado de registros bancarios
const bankRecords = ref([]);
const summary = ref({ total_ingresos: 0, total_egresos: 0, saldo_neto: 0 });
const pagination = ref({ current_page: 1, last_page: 1, total: 0 });
const loading = ref(false);

// Filtros
const filters = ref({
    id_bank_account: '',
    periodo: '',
    conciliated: '0',
    page: 1,
    per_page: 20
});

const fetchBankRecords = async (page = 1) => {
    if (props.permissions?.banks_read === false || props.permissions?.bank_records_read === false) {
        return;
    }
    loading.value = true;
    filters.value.page = page;
    try {
        const res = await axios.get(route('financiera-banks.records'), {
            params: filters.value
        });
        bankRecords.value = res.data.records.data;
        summary.value = res.data.summary;
        pagination.value = {
            current_page: res.data.records.current_page,
            last_page: res.data.records.last_page,
            total: res.data.records.total
        };
    } catch (err) {
        if (err.response?.status !== 403) {
            console.error('Error al cargar registros bancarios:', err);
        }
    } finally {
        loading.value = false;
    }
};

// Modal de Crear / Editar Cuenta Bancaria
const showAccountModal = ref(false);
const isEditingAccount = ref(false);
const savingAccount = ref(false);
const accountError = ref('');
const showIntegrationFields = ref(false); // Por defecto oculto según requerimiento del usuario

const accountForm = ref({
    id: null,
    bank_name: '',
    label: '',
    bank_code: '',
    account_type: '0', // 0: Ahorros, 1: Corriente, 2: Caja/Efectivo
    currency_code: 'PEN',
    account_number: '',
    number_cci: '',
    titular_name: '',
    position: 1,
    note: '',
    // Campos de integración bancaria
    integration_account_number: '',
    integration_code_service: '',
    integration_concept_code: '01',
    integration_concept_description: 'SERVICIO ENSEÑANZA',
    integration_abono_account: '',
    integration_name_file: '',
    integration_indicador_ca: '1',
    integration_pago_parcial: '1'
});

const openCreateAccountModal = () => {
    isEditingAccount.value = false;
    accountError.value = '';
    showIntegrationFields.value = false;
    accountForm.value = {
        id: null,
        bank_name: 'BCP',
        label: 'Cuenta Principal',
        bank_code: '',
        account_type: '0',
        currency_code: 'PEN',
        account_number: '',
        number_cci: '',
        titular_name: '',
        position: (accounts.value?.length || 0) + 1,
        note: '',
        integration_account_number: '',
        integration_code_service: '',
        integration_concept_code: '01',
        integration_concept_description: 'SERVICIO ENSEÑANZA',
        integration_abono_account: '',
        integration_name_file: '',
        integration_indicador_ca: '1',
        integration_pago_parcial: '1'
    };
    showAccountModal.value = true;
};

const openEditAccountModal = (ba) => {
    isEditingAccount.value = true;
    accountError.value = '';
    // Si la cuenta ya tiene configurada integración, abrir por conveniencia o mantener oculto
    showIntegrationFields.value = !!ba.integration_account_number || !!ba.integration_code_service;
    accountForm.value = {
        id: ba.id,
        bank_name: ba.bank_name || '',
        label: ba.label || '',
        bank_code: ba.bank_code || '',
        account_type: String(ba.account_type ?? '0'),
        currency_code: ba.currency_code || 'PEN',
        account_number: ba.account_number || '',
        number_cci: ba.number_cci || '',
        titular_name: ba.titular_name || '',
        position: ba.position ?? 1,
        note: ba.note || '',
        integration_account_number: ba.integration_account_number || '',
        integration_code_service: ba.integration_code_service || '',
        integration_concept_code: ba.integration_concept_code || '01',
        integration_concept_description: ba.integration_concept_description || 'SERVICIO ENSEÑANZA',
        integration_abono_account: ba.integration_abono_account || '',
        integration_name_file: ba.integration_name_file || '',
        integration_indicador_ca: ba.integration_indicador_ca || '1',
        integration_pago_parcial: ba.integration_pago_parcial || '1'
    };
    showAccountModal.value = true;
};

const submitAccount = async () => {
    savingAccount.value = true;
    accountError.value = '';
    try {
        if (isEditingAccount.value) {
            await axios.put(route('financiera-bank-accounts.update', accountForm.value.id), accountForm.value);
        } else {
            await axios.post(route('financiera-bank-accounts.store'), accountForm.value);
        }
        showAccountModal.value = false;
        fetchBankAccounts();
    } catch (err) {
        accountError.value = err.response?.data?.message || 'Error al guardar la cuenta bancaria.';
    } finally {
        savingAccount.value = false;
    }
};

const deleteAccount = async (ba) => {
    if (!confirm(`¿Está seguro de eliminar o desactivar la cuenta ${ba.bank_name} - ${ba.label}?`)) return;
    try {
        const res = await axios.delete(route('financiera-bank-accounts.destroy', ba.id));
        alert(res.data.message);
        fetchBankAccounts();
        if (filters.value.id_bank_account === ba.id) {
            filters.value.id_bank_account = '';
            fetchBankRecords(1);
        }
    } catch (err) {
        alert(err.response?.data?.message || 'Error al eliminar la cuenta bancaria.');
    }
};

// Modal de Nuevo Movimiento Bancario
const showRecordModal = ref(false);
const savingRecord = ref(false);
const recordError = ref('');
const recordForm = ref({
    id_bank_account: '',
    fecha_operacion: new Date().toISOString().substring(0, 10),
    type: 1, // 1: Ingreso, 0: Egreso
    amount: 150.00,
    description: '',
    operation_number: '',
    periodo: '',
    note: ''
});

const openCreateRecordModal = () => {
    recordError.value = '';
    recordForm.value = {
        id_bank_account: accounts.value.length > 0 ? accounts.value[0].id : '',
        fecha_operacion: new Date().toISOString().substring(0, 10),
        type: 1,
        amount: 150.00,
        description: 'Abono / Depósito Bancario',
        operation_number: '',
        periodo: `${props.anioEscolar || new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
        note: ''
    };
    showRecordModal.value = true;
};

const submitRecord = async () => {
    savingRecord.value = true;
    recordError.value = '';
    try {
        await axios.post(route('financiera-banks.store-record'), recordForm.value);
        showRecordModal.value = false;
        fetchBankRecords(1);
    } catch (err) {
        recordError.value = err.response?.data?.message || 'Error al registrar movimiento bancario.';
    } finally {
        savingRecord.value = false;
    }
};

// Modal de Conciliación Bancaria
const showReconcileModal = ref(false);
const activeBankRecord = ref(null);
const candidatePayments = ref([]);
const loadingCandidates = ref(false);

const openReconcileModal = async (record) => {
    activeBankRecord.value = record;
    showReconcileModal.value = true;
    loadingCandidates.value = true;
    candidatePayments.value = [];

    try {
        const res = await axios.get(route('financiera-payments.list'), {
            params: {
                search: record.operation_number || '',
                id_bank_account: record.id_bank_account,
                per_page: 20
            }
        });
        candidatePayments.value = res.data.data;
    } catch (err) {
        console.error('Error al buscar pagos para conciliar:', err);
    } finally {
        loadingCandidates.value = false;
    }
};

const reconcileWithPayment = async (paymentId) => {
    if (!activeBankRecord.value) return;
    try {
        await axios.post(route('financiera-banks.reconcile'), {
            id_bank: activeBankRecord.value.id,
            id_payment: paymentId
        });
        showReconcileModal.value = false;
        fetchBankRecords(pagination.value.current_page);
    } catch (err) {
        alert(err.response?.data?.message || 'Error al conciliar.');
    }
};

const unreconcileRecord = async (record) => {
    if (!confirm('¿Desea desconciliar este movimiento bancario?')) return;
    try {
        await axios.post(route('financiera-banks.unreconcile', record.id));
        fetchBankRecords(pagination.value.current_page);
    } catch (err) {
        alert(err.response?.data?.message || 'Error al desconciliar.');
    }
};

// Selección y Conciliación Masiva de Movimientos Bancarios
const selectedBankRecordIds = ref([]);
const isReconcilingMassive = ref(false);

const isAllRecordsSelected = computed(() => {
    return bankRecords.value.length > 0 && bankRecords.value.every(r => selectedBankRecordIds.value.includes(r.id));
});

const toggleSelectAllRecords = () => {
    if (isAllRecordsSelected.value) {
        selectedBankRecordIds.value = [];
    } else {
        selectedBankRecordIds.value = bankRecords.value.map(r => r.id);
    }
};

const toggleSelectRecord = (id) => {
    const idx = selectedBankRecordIds.value.indexOf(id);
    if (idx > -1) {
        selectedBankRecordIds.value.splice(idx, 1);
    } else {
        selectedBankRecordIds.value.push(id);
    }
};

const reconcileMassiveRecords = async (allFiltered = false) => {
    const count = allFiltered ? pagination.value.total : selectedBankRecordIds.value.length;
    if (count === 0) return;

    const message = allFiltered
        ? `¿Está seguro de conciliar TODOS los ${count} movimientos bancarios filtrados?`
        : `¿Está seguro de conciliar los ${count} movimientos bancarios seleccionados?`;

    if (!confirm(message)) return;

    isReconcilingMassive.value = true;
    try {
        const res = await axios.post(route('financiera-banks.reconcile-massive'), {
            record_ids: selectedBankRecordIds.value,
            all_filtered: allFiltered,
            ...filters.value
        });
        alert(res.data.message);
        selectedBankRecordIds.value = [];
        fetchBankRecords(pagination.value.current_page);
    } catch (err) {
        alert(err.response?.data?.message || 'Error al conciliar movimientos bancarios.');
    } finally {
        isReconcilingMassive.value = false;
    }
};

const getAccountTypeLabel = (type) => {
    if (type === '0' || type === 0) return 'Cuenta de Ahorros';
    if (type === '1' || type === 1) return 'Cuenta Corriente';
    if (type === '2' || type === 2) return 'Caja / Efectivo';
    return 'Cuenta Bancaria';
};

onMounted(() => {
    if (accounts.value.length > 0) {
        filters.value.id_bank_account = accounts.value[0].id;
    }
    fetchBankRecords(1);
});
</script>

<template>
    <div class="space-y-6">
        <!-- Barra Superior de Cuentas Bancarias con Botón de Creación -->
        <div class="flex items-center justify-between gap-4">
            <div>
                <h3 class="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                    <Icon icon="tabler:building-bank" class="text-indigo-600 dark:text-indigo-400 w-5 h-5" />
                    <span>Cuentas Bancarias y Cajas ({{ accounts.length }})</span>
                </h3>
                <p class="text-xs text-slate-400">Seleccione una cuenta para filtrar los movimientos o agregue una nueva cuenta</p>
            </div>
            <button v-if="permissions?.bank_accounts_write !== false" @click="openCreateAccountModal" class="btn-premium-primary inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-md transition-all">
                <Icon icon="tabler:plus" class="w-4 h-4" />
                <span>Nueva Cuenta Bancaria</span>
            </button>
        </div>

        <!-- Cuentas Bancarias Cards Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div v-for="ba in accounts" :key="ba.id" class="p-4 rounded-2xl border transition-all relative overflow-hidden group" :class="filters.id_bank_account === ba.id ? 'bg-indigo-50/80 dark:bg-indigo-950/40 border-indigo-500 shadow-md ring-2 ring-indigo-500/20' : 'bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'">
                <!-- Header de la tarjeta -->
                <div class="flex items-start justify-between">
                    <div class="cursor-pointer flex-1" @click="filters.id_bank_account = (filters.id_bank_account === ba.id ? '' : ba.id); fetchBankRecords(1);">
                        <div class="flex items-center gap-1.5 flex-wrap">
                            <span class="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" :class="ba.account_type == 2 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300' : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'">
                                {{ getAccountTypeLabel(ba.account_type) }}
                            </span>
                            <span class="text-[9px] font-bold font-mono px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
                                Pos. #{{ ba.position || 1 }}
                            </span>
                        </div>
                        <h4 class="font-bold text-slate-800 dark:text-white mt-1.5 text-sm">{{ ba.bank_name || 'Banco' }}</h4>
                        <p class="text-xs text-slate-500 font-medium truncate max-w-[170px]">{{ ba.label }}</p>
                    </div>

                    <!-- Icono o Acciones rápidas -->
                    <div v-if="permissions?.bank_accounts_write !== false" class="flex items-center gap-1">
                        <button @click.stop="openEditAccountModal(ba)" title="Editar Cuenta" class="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 opacity-80 hover:opacity-100 transition-all">
                            <Icon icon="tabler:pencil" class="w-3.5 h-3.5" />
                        </button>
                        <button @click.stop="deleteAccount(ba)" title="Eliminar / Desactivar Cuenta" class="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 opacity-80 hover:opacity-100 transition-all">
                            <Icon icon="tabler:trash" class="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>

                <!-- Footer de la tarjeta -->
                <div class="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between text-xs cursor-pointer" @click="filters.id_bank_account = (filters.id_bank_account === ba.id ? '' : ba.id); fetchBankRecords(1);">
                    <span class="text-slate-400 font-mono text-[11px] truncate max-w-[150px]">{{ ba.account_number || 'Sin número' }}</span>
                    <span class="font-bold text-indigo-600 dark:text-indigo-400">{{ ba.currency_code || 'PEN' }}</span>
                </div>
            </div>
        </div>

        <!-- Resumen de Movimientos y Filtros -->
        <div class="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700 shadow-sm space-y-4">
            <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div class="flex items-center gap-3">
                    <button v-if="permissions?.bank_records_write !== false" @click="openCreateRecordModal" class="btn-premium-primary inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-md transition-all">
                        <Icon icon="tabler:plus" class="w-5 h-5" />
                        <span>Nuevo Movimiento Bancario</span>
                    </button>
                </div>

                <!-- KPIs de la cuenta filtrada -->
                <div class="flex flex-wrap items-center gap-4 text-xs font-semibold">
                    <div class="px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-800/40 flex items-center gap-1.5">
                        <Icon icon="tabler:arrow-up-right" class="w-4 h-4 text-emerald-600" />
                        <span>Ingresos: S/ {{ Number(summary.total_ingresos).toFixed(2) }}</span>
                    </div>
                    <div class="px-3 py-1.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-200/60 dark:border-rose-800/40 flex items-center gap-1.5">
                        <Icon icon="tabler:arrow-down-left" class="w-4 h-4 text-rose-600" />
                        <span>Egresos: S/ {{ Number(summary.total_egresos).toFixed(2) }}</span>
                    </div>
                    <div class="px-3 py-1.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200/60 dark:border-indigo-800/40 flex items-center gap-1.5">
                        <Icon icon="tabler:wallet" class="w-4 h-4 text-indigo-600" />
                        <span>Saldo Neto: S/ {{ Number(summary.saldo_neto).toFixed(2) }}</span>
                    </div>
                </div>
            </div>

            <!-- Filtros -->
            <div class="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-3 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                <div>
                    <select v-model="filters.id_bank_account" @change="fetchBankRecords(1)" class="w-full py-2 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200">
                        <option value="">Todas las cuentas</option>
                        <option v-for="ba in accounts" :key="ba.id" :value="ba.id">{{ ba.bank_name }} - {{ ba.label }}</option>
                    </select>
                </div>

                <div>
                    <input type="text" v-model="filters.periodo" @change="fetchBankRecords(1)" placeholder="Periodo Extracto (ej. 2026-08)" class="w-full py-2 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                </div>

                <div>
                    <select v-model="filters.conciliated" @change="fetchBankRecords(1)" class="w-full py-2 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200">
                        <option value="all">Todos los registros</option>
                        <option value="1">Conciliados con pago</option>
                        <option value="0">Pendientes de conciliación</option>
                    </select>
                </div>

                <div>
                    <button @click="filters = { id_bank_account: '', periodo: '', conciliated: '0', page: 1, per_page: 20 }; fetchBankRecords(1);" class="w-full py-2 px-3 text-xs font-semibold rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-1.5">
                        <Icon icon="tabler:refresh" class="w-4 h-4" />
                        <span>Actualizar / Limpiar</span>
                    </button>
                </div>
            </div>

            <!-- Barra flotante de selección y conciliación masiva -->
            <div v-if="selectedBankRecordIds.length > 0" class="p-3 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 rounded-xl flex flex-wrap items-center justify-between gap-3 animate-in fade-in duration-150">
                <div class="flex items-center gap-2 text-xs font-bold text-emerald-900 dark:text-emerald-200">
                    <Icon icon="tabler:checkbox" class="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    <span>{{ selectedBankRecordIds.length }} movimiento(s) bancario(s) seleccionado(s)</span>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                    <button v-if="permissions?.bank_records_write !== false" @click="reconcileMassiveRecords(false)" :disabled="isReconcilingMassive" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all shadow-xs disabled:opacity-50">
                        <Icon v-if="isReconcilingMassive" icon="tabler:loader-2" class="w-3.5 h-3.5 animate-spin" />
                        <Icon v-else icon="tabler:circle-check" class="w-3.5 h-3.5" />
                        <span>Conciliar Seleccionados</span>
                    </button>
                    <button v-if="permissions?.bank_records_write !== false" @click="reconcileMassiveRecords(true)" :disabled="isReconcilingMassive" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 rounded-lg transition-all border border-emerald-300 disabled:opacity-50">
                        <Icon icon="tabler:checks" class="w-3.5 h-3.5" />
                        <span>Conciliar Todos los Filtrados ({{ pagination.total }})</span>
                    </button>
                    <button @click="selectedBankRecordIds = []" class="px-2.5 py-1.5 text-xs font-semibold bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-100">
                        Deseleccionar
                    </button>
                </div>
            </div>
        </div>

        <!-- Tabla de Movimientos Bancarios -->
        <div class="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden">
            <div v-if="loading" class="p-12 text-center text-slate-400 flex flex-col items-center justify-center gap-3">
                <Icon icon="tabler:loader-2" class="w-8 h-8 animate-spin text-indigo-500" />
                <span class="text-sm font-medium">Cargando movimientos bancarios...</span>
            </div>

            <div v-else-if="bankRecords.length === 0" class="p-12 text-center text-slate-400 flex flex-col items-center justify-center gap-3">
                <Icon icon="tabler:building-bank" class="w-12 h-12 text-slate-300 dark:text-slate-600" />
                <p class="text-base font-semibold text-slate-600 dark:text-slate-300">No hay movimientos bancarios registrados</p>
                <p class="text-xs text-slate-400 max-w-sm">Registre los movimientos del extracto bancario para conciliar los pagos recibidos.</p>
            </div>

            <div v-else class="overflow-x-auto">
                <table class="w-full text-left text-xs">
                    <thead class="bg-slate-50 dark:bg-slate-900/60 text-slate-500 font-semibold uppercase tracking-wider border-b border-slate-100 dark:border-slate-700">
                        <tr>
                            <th class="py-3.5 px-3 text-center w-10">
                                <input type="checkbox" :checked="isAllRecordsSelected" @change="toggleSelectAllRecords" class="rounded text-emerald-600 focus:ring-emerald-500" />
                            </th>
                            <th class="py-3.5 px-4">Fecha / Periodo</th>
                            <th class="py-3.5 px-4">Cuenta Bancaria</th>
                            <th class="py-3.5 px-4">Operación / Glosa</th>
                            <th class="py-3.5 px-4 text-center">Tipo</th>
                            <th class="py-3.5 px-4 text-right">Monto</th>
                            <th class="py-3.5 px-4 text-right">Saldo Cuenta</th>
                            <th class="py-3.5 px-4 text-center">Conciliación</th>
                            <th class="py-3.5 px-4 text-center">Acciones</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100 dark:divide-slate-700/60 font-medium">
                        <tr v-for="rec in bankRecords" :key="rec.id" class="hover:bg-slate-50/60 dark:hover:bg-slate-750 transition-colors" :class="{'bg-emerald-50/30 dark:bg-emerald-950/20': selectedBankRecordIds.includes(rec.id)}">
                            <td class="py-3 px-3 text-center" @click.stop>
                                <input type="checkbox" :checked="selectedBankRecordIds.includes(rec.id)" @change="toggleSelectRecord(rec.id)" class="rounded text-emerald-600 focus:ring-emerald-500" />
                            </td>
                            <td class="py-3 px-4">
                                <div class="font-bold text-slate-800 dark:text-white">{{ rec.fecha_operacion }}</div>
                                <div class="text-[11px] text-slate-400">Extracto: {{ rec.periodo }}</div>
                            </td>

                            <td class="py-3 px-4">
                                <div class="font-semibold text-indigo-600 dark:text-indigo-400">{{ rec.bank_account?.bank_name }}</div>
                                <div class="text-[11px] text-slate-500">{{ rec.bank_account?.account_number }}</div>
                            </td>

                            <td class="py-3 px-4">
                                <div class="font-semibold text-slate-800 dark:text-slate-200">{{ rec.description }}</div>
                                <div class="text-[11px] text-slate-400">Op: {{ rec.operation_number || 'S/N' }}</div>
                            </td>

                            <td class="py-3 px-4 text-center">
                                <span v-if="rec.type == 1" class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
                                    Haber / Ingreso
                                </span>
                                <span v-else class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300">
                                    Debe / Egreso
                                </span>
                            </td>

                            <td class="py-3 px-4 text-right font-bold" :class="rec.type == 1 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'">
                                {{ rec.type == 1 ? '+' : '-' }} S/ {{ Number(rec.amount).toFixed(2) }}
                            </td>

                            <td class="py-3 px-4 text-right font-semibold text-slate-700 dark:text-slate-300">
                                S/ {{ Number(rec.balance).toFixed(2) }}
                            </td>

                            <!-- Estado Conciliación -->
                            <td class="py-3 px-4 text-center">
                                <div v-if="rec.conciliated" class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
                                    <Icon icon="tabler:circle-check" class="w-3.5 h-3.5" />
                                    <span>Conciliado ({{ rec.payment?.ref }})</span>
                                </div>
                                <div v-else class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300">
                                    <Icon icon="tabler:clock" class="w-3.5 h-3.5" />
                                    <span>Pendiente</span>
                                </div>
                            </td>

                            <!-- Acciones -->
                            <td class="py-3 px-4 text-center">
                                <div v-if="permissions?.bank_records_write !== false" class="flex items-center justify-center gap-1.5">
                                    <button v-if="!rec.conciliated" @click="openReconcileModal(rec)" class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 font-semibold text-xs transition-colors">
                                        <Icon icon="tabler:arrows-join" class="w-3.5 h-3.5" />
                                        <span>Conciliar</span>
                                    </button>
                                    <button v-else @click="unreconcileRecord(rec)" title="Desconciliar" class="p-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                                        <Icon icon="tabler:x" class="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Paginación -->
            <div v-if="pagination.last_page > 1" class="px-5 py-3.5 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                <span class="text-xs text-slate-500">Página {{ pagination.current_page }} de {{ pagination.last_page }}</span>
                <div class="flex items-center gap-1.5">
                    <button :disabled="pagination.current_page <= 1" @click="fetchBankRecords(pagination.current_page - 1)" class="px-3 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 hover:bg-white dark:hover:bg-slate-800">Anterior</button>
                    <button :disabled="pagination.current_page >= pagination.last_page" @click="fetchBankRecords(pagination.current_page + 1)" class="px-3 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 hover:bg-white dark:hover:bg-slate-800">Siguiente</button>
                </div>
            </div>
        </div>

        <!-- Modal Crear / Editar Cuenta Bancaria -->
        <div v-if="showAccountModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
            <div class="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-700 space-y-4 my-8 animate-in fade-in zoom-in-95 duration-200">
                <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-700">
                    <div class="flex items-center gap-2.5">
                        <div class="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400">
                            <Icon icon="tabler:building-bank" class="w-6 h-6" />
                        </div>
                        <div>
                            <h3 class="text-base font-bold text-slate-800 dark:text-white">
                                {{ isEditingAccount ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria' }}
                            </h3>
                            <p class="text-xs text-slate-400">Configure los datos generales y de integración de la cuenta</p>
                        </div>
                    </div>
                    <button @click="showAccountModal = false" class="text-slate-400 hover:text-slate-600 rounded-lg p-1">
                        <Icon icon="tabler:x" class="w-5 h-5" />
                    </button>
                </div>

                <div v-if="accountError" class="p-3 rounded-xl text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                    {{ accountError }}
                </div>

                <form @submit.prevent="submitAccount" class="space-y-4 text-xs">
                    <!-- Datos Generales -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Entidad Financiera / Banco *</label>
                            <input type="text" v-model="accountForm.bank_name" required placeholder="Ej. BCP, BBVA, Interbank, Caja..." class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-semibold" />
                        </div>
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Etiqueta / Nombre Descriptivo *</label>
                            <input type="text" v-model="accountForm.label" required placeholder="Ej. Cuenta Recaudadora Principal" class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                        </div>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Tipo de Cuenta *</label>
                            <select v-model="accountForm.account_type" required class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-semibold">
                                <option value="0">Cuenta de Ahorros</option>
                                <option value="1">Cuenta Corriente</option>
                                <option value="2">Caja / Efectivo</option>
                            </select>
                        </div>
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Moneda *</label>
                            <select v-model="accountForm.currency_code" required class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-semibold">
                                <option value="PEN">Soles (PEN - S/)</option>
                                <option value="USD">Dólares (USD - $)</option>
                            </select>
                        </div>
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Código Banco (Opcional)</label>
                            <input type="text" v-model="accountForm.bank_code" placeholder="Ej. 002" class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                        </div>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Número de Cuenta</label>
                            <input type="text" v-model="accountForm.account_number" placeholder="Ej. 191-23847291-0-12" class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-mono" />
                        </div>
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">CCI (Interbancario)</label>
                            <input type="text" v-model="accountForm.number_cci" placeholder="Ej. 002-191-0023847291012-54" class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-mono" />
                        </div>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Titular de la Cuenta</label>
                            <input type="text" v-model="accountForm.titular_name" placeholder="Ej. UnensEdu S.A.C." class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                        </div>
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Posición / Orden *</label>
                            <input type="number" min="1" v-model="accountForm.position" placeholder="1, 2, 3..." class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-bold" />
                        </div>
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Notas / Observaciones</label>
                            <input type="text" v-model="accountForm.note" placeholder="Opcional..." class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                        </div>
                    </div>

                    <!-- SECCIÓN DESPLEGABLE: Configuración de Integración Bancaria (Oculta por defecto) -->
                    <div class="pt-2 border-t border-slate-100 dark:border-slate-700">
                        <button type="button" @click="showIntegrationFields = !showIntegrationFields" class="w-full flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 dark:bg-slate-900/60 dark:hover:bg-slate-900 text-slate-700 dark:text-slate-300 transition-colors">
                            <span class="flex items-center gap-2 font-bold text-xs">
                                <Icon icon="tabler:adjustments-horizontal" class="w-4 h-4 text-indigo-500" />
                                <span>Configuración de Integración Bancaria (Recaudación / TXT)</span>
                            </span>
                            <span class="text-xs text-indigo-600 dark:text-indigo-400 font-semibold flex items-center gap-1">
                                <span>{{ showIntegrationFields ? 'Ocultar campos' : 'Mostrar campos' }}</span>
                                <Icon :icon="showIntegrationFields ? 'tabler:chevron-up' : 'tabler:chevron-down'" class="w-4 h-4" />
                            </span>
                        </button>

                        <div v-show="showIntegrationFields" class="mt-3 p-4 rounded-xl border border-slate-200 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-900/40 space-y-3 animate-in fade-in duration-200">
                            <p class="text-[11px] text-slate-400 italic">Campos utilizados para la generación y carga automática de archivos de recaudación bancaria (TXT de cobros).</p>

                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label class="block font-semibold text-slate-600 dark:text-slate-400 mb-1">Nro Cuenta Recaudación (integration_account_number)</label>
                                    <input type="text" v-model="accountForm.integration_account_number" placeholder="Ej. 19123847291012" class="w-full py-1.5 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200" />
                                </div>
                                <div>
                                    <label class="block font-semibold text-slate-600 dark:text-slate-400 mb-1">Código de Servicio (integration_code_service)</label>
                                    <input type="text" v-model="accountForm.integration_code_service" placeholder="Ej. 001" class="w-full py-1.5 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200" />
                                </div>
                            </div>

                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label class="block font-semibold text-slate-600 dark:text-slate-400 mb-1">Código de Concepto (integration_concept_code)</label>
                                    <input type="text" v-model="accountForm.integration_concept_code" placeholder="Ej. 01" class="w-full py-1.5 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200" />
                                </div>
                                <div>
                                    <label class="block font-semibold text-slate-600 dark:text-slate-400 mb-1">Descripción Concepto (integration_concept_description)</label>
                                    <input type="text" v-model="accountForm.integration_concept_description" placeholder="Ej. SERVICIO ENSEÑANZA" class="w-full py-1.5 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200" />
                                </div>
                            </div>

                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label class="block font-semibold text-slate-600 dark:text-slate-400 mb-1">Cuenta de Abono (integration_abono_account)</label>
                                    <input type="text" v-model="accountForm.integration_abono_account" placeholder="Ej. 19123847291012" class="w-full py-1.5 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200" />
                                </div>
                                <div>
                                    <label class="block font-semibold text-slate-600 dark:text-slate-400 mb-1">Nombre Archivo (integration_name_file)</label>
                                    <input type="text" v-model="accountForm.integration_name_file" placeholder="Ej. RECAUDACION" class="w-full py-1.5 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200" />
                                </div>
                            </div>

                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label class="block font-semibold text-slate-600 dark:text-slate-400 mb-1">Indicador Cobro Automático (integration_indicador_ca)</label>
                                    <select v-model="accountForm.integration_indicador_ca" class="w-full py-1.5 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200">
                                        <option value="1">1 - Por fecha de encabezado</option>
                                        <option value="2">2 - Por detalle</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block font-semibold text-slate-600 dark:text-slate-400 mb-1">Acepta Pago Parcial (integration_pago_parcial)</label>
                                    <select v-model="accountForm.integration_pago_parcial" class="w-full py-1.5 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200">
                                        <option value="1">1 - Sí se acepta pago parcial</option>
                                        <option value="0">0 - No se acepta pago parcial</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Botones -->
                    <div class="flex items-center justify-end gap-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                        <button type="button" @click="showAccountModal = false" class="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700">Cancelar</button>
                        <button type="submit" :disabled="savingAccount" class="btn-premium-primary px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold flex items-center gap-2 shadow-md">
                            <Icon v-if="savingAccount" icon="tabler:loader-2" class="w-4 h-4 animate-spin" />
                            <span>{{ isEditingAccount ? 'Guardar Cambios' : 'Crear Cuenta' }}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Modal Registrar Movimiento Bancario -->
        <div v-if="showRecordModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div class="bg-white dark:bg-slate-800 rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-700 space-y-4 animate-in fade-in zoom-in-95 duration-200">
                <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-700">
                    <h3 class="text-base font-bold text-slate-800 dark:text-white">Nuevo Movimiento Bancario</h3>
                    <button @click="showRecordModal = false" class="text-slate-400 hover:text-slate-600 rounded-lg p-1">
                        <Icon icon="tabler:x" class="w-5 h-5" />
                    </button>
                </div>

                <div v-if="recordError" class="p-3 rounded-xl text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                    {{ recordError }}
                </div>

                <form @submit.prevent="submitRecord" class="space-y-3.5 text-xs">
                    <div>
                        <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Cuenta Bancaria</label>
                        <select v-model="recordForm.id_bank_account" required class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200">
                            <option value="">Seleccione cuenta</option>
                            <option v-for="ba in accounts" :key="ba.id" :value="ba.id">{{ ba.bank_name }} - {{ ba.label }}</option>
                        </select>
                    </div>

                    <div class="grid grid-cols-2 gap-3">
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Tipo de Movimiento</label>
                            <select v-model="recordForm.type" required class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-bold">
                                <option :value="1">Ingreso / Haber (+)</option>
                                <option :value="0">Egreso / Debe (-)</option>
                            </select>
                        </div>
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Monto (S/)</label>
                            <input type="number" step="0.01" min="0.01" v-model="recordForm.amount" required class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-bold" />
                        </div>
                    </div>

                    <div>
                        <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Glosa / Descripción</label>
                        <input type="text" v-model="recordForm.description" required placeholder="Ej. Depósito transferencias bancarias" class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                    </div>

                    <div class="grid grid-cols-2 gap-3">
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Fecha Operación</label>
                            <input type="date" v-model="recordForm.fecha_operacion" required class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                        </div>
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Nro Operación</label>
                            <input type="text" v-model="recordForm.operation_number" placeholder="Ej. 109283" class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                        </div>
                    </div>

                    <div class="flex items-center justify-end gap-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                        <button type="button" @click="showRecordModal = false" class="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700">Cancelar</button>
                        <button type="submit" :disabled="savingRecord" class="btn-premium-primary px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold flex items-center gap-2 shadow-md">
                            <Icon v-if="savingRecord" icon="tabler:loader-2" class="w-4 h-4 animate-spin" />
                            <span>Guardar Movimiento</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Modal Conciliación con Pagos -->
        <div v-if="showReconcileModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div class="bg-white dark:bg-slate-800 rounded-2xl max-w-xl w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-700 space-y-4 animate-in fade-in zoom-in-95 duration-200">
                <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-700">
                    <div class="flex items-center gap-2">
                        <div class="p-2 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400">
                            <Icon icon="tabler:arrows-join" class="w-5 h-5" />
                        </div>
                        <div>
                            <h3 class="text-base font-bold text-slate-800 dark:text-white">Conciliación Bancaria</h3>
                            <p class="text-xs text-slate-400">Seleccione el pago del sistema que corresponde a este movimiento</p>
                        </div>
                    </div>
                    <button @click="showReconcileModal = false" class="text-slate-400 hover:text-slate-600 rounded-lg p-1">
                        <Icon icon="tabler:x" class="w-5 h-5" />
                    </button>
                </div>

                <!-- Detalle del movimiento bancario -->
                <div class="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 text-xs space-y-1">
                    <div class="flex justify-between font-bold">
                        <span>{{ activeBankRecord?.description }}</span>
                        <span class="text-emerald-600">S/ {{ Number(activeBankRecord?.amount).toFixed(2) }}</span>
                    </div>
                    <div class="text-slate-400">
                        Fecha: {{ activeBankRecord?.fecha_operacion }} | Nro Op: <strong>{{ activeBankRecord?.operation_number || 'S/N' }}</strong>
                    </div>
                </div>

                <!-- Pagos candidatos -->
                <div class="space-y-2 max-h-60 overflow-y-auto">
                    <div v-if="loadingCandidates" class="p-6 text-center text-slate-400 flex items-center justify-center gap-2">
                        <Icon icon="tabler:loader-2" class="w-4 h-4 animate-spin text-indigo-500" />
                        <span class="text-xs">Buscando pagos registrados...</span>
                    </div>

                    <div v-else-if="candidatePayments.length === 0" class="p-6 text-center text-slate-400 text-xs">
                        No se encontraron pagos registrados con ese número de operación o cuenta.
                    </div>

                    <div v-else v-for="pay in candidatePayments" :key="pay.id" class="flex items-center justify-between p-3 rounded-xl border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-750 transition-colors">
                        <div>
                            <div class="font-bold text-xs text-slate-800 dark:text-white">{{ pay.ref }} - {{ pay.student ? (pay.student.person?.apellidos + ' ' + pay.student.person?.nombres) : 'Pago General' }}</div>
                            <div class="text-[11px] text-slate-400">Fecha: {{ pay.fecha_pago }} | Op: {{ pay.operation_number }} | Monto: <strong class="text-indigo-600">S/ {{ Number(pay.amount).toFixed(2) }}</strong></div>
                        </div>
                        <button @click="reconcileWithPayment(pay.id)" class="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs shadow-sm flex items-center gap-1">
                            <Icon icon="tabler:check" class="w-3.5 h-3.5" />
                            <span>Vincular</span>
                        </button>
                    </div>
                </div>

                <div class="flex justify-end pt-3 border-t border-slate-100 dark:border-slate-700">
                    <button @click="showReconcileModal = false" class="px-4 py-2 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200">Cerrar</button>
                </div>
            </div>
        </div>
    </div>
</template>
